import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, inject, Input, input, OnInit, signal, ViewChild } from '@angular/core';
import { Pricing, PricingProduct, Work } from '../../../../models/work.model';
import { ProviderService } from '../../../../providers/provider.service';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ProviderFormComponent } from '../../../../providers/provider-form/provider-form.component';
import { catchError } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FirebaseService } from '../../../../services/firebase.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { WorkService } from '../../../work.service';
import { animate, state, style, transition, trigger } from '@angular/animations';

@Component({
    selector: 'wpl-inventory-action-for-pricing',
    templateUrl: './inventory-action-for-pricing.component.html',
    styleUrl: './inventory-action-for-pricing.component.scss',
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0', padding: '0' })),
            state('expanded', style({ height: '*', minHeight: "*", paddingTop: '1em', paddingBottom: '1em' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false
})
export class InventoryActionForPricingComponent implements OnInit {
  sending = signal(false);

  @Input({required: true}) workInstance: Work;

  private readonly fb = inject(FormBuilder);

  form = this.fb.group({
    provider_ids: this.fb.control([], { validators: Validators.required }),
  });

  public productsIds: string[] = [];

  public chosenProducts = [];

  public displayedColumns: string[] = ['product_name'];

  public displayedInnerColumns: string[] = [
    'select',
    'provider_name',
    'amount',
    'quantity',
    'total',
    'estimated_completion_date',
    'expiration_date',
    'flexible',
  ];

  public mapProductPricing: Record<number, any> = {};

  public expandedElement: Pricing | null;

  private readonly providerService = inject(ProviderService);

  providers = this.providerService.providers;

  private readonly snackbar = inject(MatSnackBar);

  private readonly firebaseService = inject(FirebaseService);

  private readonly workService = inject(WorkService);

  private readonly cdr = inject(ChangeDetectorRef);

  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(MatPaginator) paginator: MatPaginator;
  
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.mapProductPricing = this.workInstance.sent_pricings.reduce((acc, pricing) => {
      pricing.products.forEach((product) => {
        const pricingProduct = {
          ...product,
          offered_quantity: product.quantity,
          ownerId: pricing.owner.id,
          ownerName: pricing.owner.name,
          expiration_date: pricing.expiration_date,
          estimated_completion_date: pricing.estimated_completion_date,
        }
        if(acc[product.product_id]) {
          acc[product.product_id].push(pricingProduct);
        } else {
          acc[product.product_id] = [pricingProduct];
        }
      });

      return acc;
    }, {});

    this.productsIds = Object.keys(this.mapProductPricing);
    this.firebaseService.subscribeToMessages(
      'pricing-request',
      { id: this.workInstance.id, type: this.workInstance.type },
      this.handlePricingRequestMessage.bind(this),
      this.destroyRef,
    );
  }

  handlePricingRequestMessage() {
    this.snackbar.open('Solicitudes de cotización enviadas', 'OK')._dismissAfter(2000);
    this.sending.set(false);
    this.refreshData();
  }

  refreshData() {
    this.workService.get(this.workInstance.id).subscribe((response: any) => {
      this.cdr.markForCheck();
      this.workInstance.pending_pricings_count = response.body.pending_pricings_count;
    });
  }

  addProvider() {
    this.dialog.open(ProviderFormComponent, {
      width: '500px',
    }).afterClosed().subscribe((provider) => {
      if (provider) {
      }
    });
  }

  selectProduct(product: PricingProduct) {
    this.chosenProducts.push(product);
    this.cdr.markForCheck();
  }

  deselectProduct(product: PricingProduct) {
    this.chosenProducts = this.chosenProducts.filter((p) => p !== product);
    this.cdr.markForCheck();
  }

  getChosenCount(product_id) {
    return this.chosenProducts
      .filter((product) => product.product_id === product_id)
      .reduce((acc, product) => acc + product.quantity, 0);
  }

  get chosenTotal() {
    return this.chosenProducts.reduce((acc, product) => acc + product.amount * product.quantity, 0);
  }

  get canSendPricingRequests(): boolean {
    if (this.sending()) {
      return false; 
    }

    const providers = this.form.get('provider_ids').value;
    if (providers?.length === 1 && providers?.at(0) === 0) {
      return false;
    }

    return this.form.valid;
  }

  checkQuantity(product) {
    if (product.quantity > product.offered_quantity) {
      product.quantity = product.offered_quantity;
    } else if (product.quantity <= 0) {
      product.quantity = 1;
    }
  }

  send_pricing_requests() {
    if (!this.form.valid) return;

    this.sending.set(true);
    this.providerService.requestPricing({
      provider_ids: this.form.value.provider_ids,
      work_instance_id: this.workInstance.id,
    })
    .pipe(
      catchError((error) => {
        this.snackbar.open('Ha ocurrido un error al enviar las solicitudes de cotización', 'Error')._dismissAfter(2000);
        console.error(error);
        return null;
      }),
    )
    .subscribe((response) => {
      this.sending.set(false);
      if (!response) return;
      this.form.reset();
    })
  }
}
